// CommitPage — /studio/:slug/:repo/_/commit/:oid
// Shows CommitHeader + DiffBoard (parent → oid).
// For root commits (no parents), shows a notice and diffs vs the empty tree.

import { useState } from "react";
import { useParams } from "react-router-dom";
import { useCommit } from "../diff/useCommit.js";
import { useGitDiff } from "../diff/useGitDiff.js";
import { DiffBoard } from "../diff/DiffBoard.js";
import { DiffPackageTabs } from "../diff/DiffPackageTabs.js";
import { DiffDiagnostics } from "../diff/DiffDiagnostics.js";
import { CommitHeader } from "./CommitHeader.js";
import { Spinner } from "../ui/Spinner.js";
import { Banner } from "../ui/Banner.js";
import { useTranslation } from "../i18n";
import "../diff/diff.css";

// Git's well-known empty-tree SHA. The diff endpoint must accept this as
// a valid "from" ref for root-commit diffs; if it does not, the embedded
// DiffBoard will show an error — this is a known server-side follow-up.
export const EMPTY_TREE_OID = "4b825dc642cb6eb9a060e54bf8d69288fbee4904";

export function CommitPage() {
  const { t } = useTranslation("history");
  const { slug = "", repo = "", oid = "" } = useParams();

  const commitQuery = useCommit({ slug, repo, oid });
  const isRootCommit =
    commitQuery.data !== undefined && commitQuery.data.parentOids.length === 0;

  const fromRef = commitQuery.data?.parentOids[0] ?? EMPTY_TREE_OID;
  const diffQuery = useGitDiff({
    slug,
    repo,
    from: fromRef,
    to: oid,
  });
  const [activeKey, setActiveKey] = useState<string | null>(null);
  const pkgs = diffQuery.data?.packages ?? [];
  const active = pkgs.find((p) => p.key === activeKey) ?? pkgs[0];

  if (commitQuery.isLoading) {
    return (
      <div className="flex justify-center p-16" aria-label={t("commit.loadingCommit")}>
        <Spinner />
      </div>
    );
  }

  if (commitQuery.error) {
    return (
      <div className="mx-auto max-w-5xl p-8">
        <Banner kind="error">{String(commitQuery.error)}</Banner>
      </div>
    );
  }

  const commit = commitQuery.data!;

  return (
    <div className="mx-auto flex max-w-5xl flex-col gap-4 p-8">
      <CommitHeader commit={commit} slug={slug} repo={repo} />

      {isRootCommit && (
        <Banner kind="info">{t("commit.initialCommit")}</Banner>
      )}

      {diffQuery.isLoading && (
        <div className="flex justify-center py-12" aria-label={t("commit.loadingDiff")}>
          <Spinner />
        </div>
      )}

      {diffQuery.error && <Banner kind="error">{String(diffQuery.error)}</Banner>}

      {diffQuery.data && !active && (
        <div className="flex justify-center py-12 text-sm text-slate-500">{t("commit.noChanges")}</div>
      )}
      {diffQuery.data && active && (
        <>
          <DiffPackageTabs packages={pkgs} activeKey={active.key} onSelect={setActiveKey} />
          <DiffDiagnostics diagnostics={active.diagnostics} />
          <DiffBoard
            beforeMods={active.before.model.modules}
            afterMods={active.after.model.modules}
            beforeEdges={active.before.edges}
            afterEdges={active.after.edges}
            changeSet={active.changeSet}
            slug={slug}
            repo={repo}
            toRef={oid}
          />
        </>
      )}
    </div>
  );
}
